import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./HeroSearch.css";

// Base URL - same domain via Nginx proxy
const API_BASE_URL = "/api";

// Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

api.interceptors.request.use(
  (config) => config,
  (error) => {
    console.error("❌ Request error:", error.message);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

const HeroSearch = () => {
  const navigate = useNavigate();

  // ---------------- STATE ----------------
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [areas, setAreas] = useState([]);

  const [selectedPropertyType, setSelectedPropertyType] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [transactionType, setTransactionType] = useState("");

  const [minSqft, setMinSqft] = useState("");
  const [maxSqft, setMaxSqft] = useState("");

  const [loading, setLoading] = useState(true);
  const [areasLoading, setAreasLoading] = useState(false);
  const [error, setError] = useState(null);

  // =====================================================
  // 1️⃣ FETCH PROPERTY TYPES (FIXED – supports array)
  // =====================================================
  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get("/ptypes");

        let data = [];

        // ✅ FIX: Normalize backend response
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (Array.isArray(response.data?.data)) {
          data = response.data.data;
        }

        const uniqueTypes = data.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.type === item.type)
        );

        setPropertyTypes(uniqueTypes);
      } catch (err) {
        console.error("Failed to fetch property types:", err);
        setError("Failed to load property types");
        setPropertyTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyTypes();
  }, []);

  // =====================================================
  // 2️⃣ FETCH AREAS AFTER PROPERTY TYPE CHANGE (FIXED)
  // =====================================================
  useEffect(() => {
    if (!selectedPropertyType) {
      setAreas([]);
      return;
    }

    const fetchAreas = async () => {
      try {
        setAreasLoading(true);

        const response = await api.get(
          `/areas?type=${encodeURIComponent(selectedPropertyType)}`
        );

        let areaData = [];

        // ✅ FIX: Normalize backend response
        if (Array.isArray(response.data)) {
          areaData = response.data;
        } else if (Array.isArray(response.data?.data)) {
          areaData = response.data.data;
        }

        setAreas(areaData);
      } catch (err) {
        console.error("Error fetching areas:", err);
        setAreas([]);
      } finally {
        setAreasLoading(false);
      }
    };

    const t = setTimeout(fetchAreas, 300);
    return () => clearTimeout(t);
  }, [selectedPropertyType]);

  // =====================================================
  // 3️⃣ SEARCH HANDLER (MIN / MAX INCLUDED)
  // =====================================================
  const handleSearch = () => {
    if (!selectedPropertyType || !selectedLocation) {
      alert("Please select both property type and location");
      return;
    }

    const params = new URLSearchParams();

    params.append("propertyType", selectedPropertyType);
    params.append("location", selectedLocation);
    params.append("transactionType", transactionType || "Rental");

    if (minSqft) params.append("minSqft", minSqft);
    if (maxSqft) params.append("maxSqft", maxSqft);

    navigate(`/properties?${params.toString()}`);
  };

  // =====================================================
  // UI
  // =====================================================
  return (
    <section className="hero-search-hero">
      <div className="search-panel">
        <div className="search-row">
          {/* PROPERTY TYPE */}
          <div className="search-field">
            <label>Property Type</label>
            <select
              className="search-select"
              value={selectedPropertyType}
              onChange={(e) => {
                setSelectedPropertyType(e.target.value);
                setSelectedLocation("");
              }}
              disabled={loading}
            >
              <option value="">
                {loading ? "Loading..." : "Select Property Type"}
              </option>
              {propertyTypes.map((t) => (
                <option key={t.id} value={t.type}>
                  {t.type}
                </option>
              ))}
            </select>
          </div>

          <div className="divider" />

          {/* LOCATION */}
          <div className="search-field">
            <label>Location</label>
            <select
              className="search-select"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              disabled={!selectedPropertyType || areasLoading}
            >
              <option value="">
                {areasLoading ? "Loading locations..." : "Select Location"}
              </option>
              {areas.map((a) => (
                <option key={a.id} value={a.area}>
                  {a.area}
                </option>
              ))}
            </select>
          </div>

          <div className="divider" />

          {/* TRANSACTION TYPE */}
          <div className="search-field">
            <label>Purchase / Rent</label>
            <select
              className="search-select"
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
            >
              <option value="">Any Type</option>
              <option value="Rental">Rental</option>
              <option value="Purchase">Purchase</option>
              <option value="ReadyToUse">Ready To Use</option>
              <option value="Land">Land</option>
            </select>
          </div>

          <div className="divider" />

          {/* MIN SQFT */}
          <div className="search-field small-input">
            <label>Min Area (Sqft)</label>
            <input
              type="number"
              className="search-select"
              placeholder="Min"
              value={minSqft}
              onChange={(e) => setMinSqft(e.target.value)}
              min="0"
            />
          </div>

          {/* MAX SQFT */}
          <div className="search-field small-input">
            <label>Max Area (Sqft)</label>
            <input
              type="number"
              className="search-select"
              placeholder="Max"
              value={maxSqft}
              onChange={(e) => setMaxSqft(e.target.value)}
              min={minSqft || "0"}
            />
          </div>

          {/* SEARCH BUTTON */}
          <button
            className="search-button"
            onClick={handleSearch}
            disabled={!selectedPropertyType || !selectedLocation}
          >
            SEARCH
          </button>
        </div>

        {error && <div className="error-text">{error}</div>}
      </div>
    </section>
  );
};

export default HeroSearch;
